import React, { useState, useEffect } from 'react';
import { Phone, Briefcase, Smartphone } from 'lucide-react';
import './CanadianPhoneInput.css';

const formatCanadianPhone = (value) => {
  const digits = value.replace(/\D/g, '');
  const clean = digits.startsWith('1') ? digits.slice(1) : digits;
  if (clean.length === 0) return '';
  if (clean.length <= 3) return `(${clean}`;
  if (clean.length <= 6) return `(${clean.slice(0, 3)}) ${clean.slice(3)}`;
  return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6, 10)}`;
};

const parsePhoneData = (value) => {
  if (!value || typeof value !== 'string') return { number: '', type: 'cell', ext: '' };
  const extMatch = value.match(/ext\.?\s*(\d+)/i);
  const ext = extMatch ? extMatch[1] : '';
  const numberPart = value.replace(/\s*ext\.?\s*\d*/i, '').trim();
  const type = value.toLowerCase().includes('work') || ext ? 'work' : 'cell';
  return { number: numberPart, type, ext };
};

const buildPhoneString = (number, type, ext) => {
  const digits = number.replace(/\D/g, '');
  if (!digits) return '';
  const clean = digits.startsWith('1') ? digits.slice(1) : digits;
  const formatted = `+1${clean}`;
  if (type === 'work' && ext) return `${formatted} ext. ${ext}`;
  return formatted;
};

const isValidCanadianPhone = (value) => {
  const digits = value.replace(/\D/g, '');
  const clean = digits.startsWith('1') ? digits.slice(1) : digits;
  if (clean.length !== 10) return false;
  const areaCode = clean.slice(0, 1);
  return areaCode !== '0' && areaCode !== '1';
};

const CanadianPhoneInput = ({
  value = '',
  onChange,
  placeholder = '(416) 555-1234',
  required = false,
  disabled = false,
  showTypeSelector = true,
  testIdPrefix = 'phone',
  label,
  className = ''
}) => {
  const parsed = parsePhoneData(value);
  const [phoneType, setPhoneType] = useState(parsed.type);
  const [displayValue, setDisplayValue] = useState(formatCanadianPhone(parsed.number));
  const [ext, setExt] = useState(parsed.ext);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    const p = parsePhoneData(value);
    setPhoneType(p.type);
    setDisplayValue(formatCanadianPhone(p.number));
    setExt(p.ext);
  }, [value]);

  const handleNumberChange = (e) => {
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, '');
    const clean = digits.startsWith('1') ? digits.slice(1) : digits;
    if (clean.length > 10) return;
    const formatted = formatCanadianPhone(raw);
    setDisplayValue(formatted);
    onChange(buildPhoneString(formatted, phoneType, ext));
  };

  const handleTypeChange = (type) => {
    setPhoneType(type);
    if (type === 'cell') {
      setExt('');
      onChange(buildPhoneString(displayValue, 'cell', ''));
    } else {
      onChange(buildPhoneString(displayValue, 'work', ext));
    }
  };

  const handleExtChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setExt(val);
    onChange(buildPhoneString(displayValue, phoneType, val));
  };

  const digits = displayValue.replace(/\D/g, '');
  const isValid = !digits || isValidCanadianPhone(displayValue);
  const showError = touched && digits.length > 0 && !isValid;

  return (
    <div className={`canadian-phone-input ${className}`} data-testid={`${testIdPrefix}-container`}>
      {label && <label className="form-label">{label}</label>}
      {showTypeSelector && (
        <div className="phone-type-selector" data-testid={`${testIdPrefix}-type-selector`}>
          <button type="button"
            className={`phone-type-btn ${phoneType === 'cell' ? 'active' : ''}`}
            onClick={() => handleTypeChange('cell')}
            data-testid={`${testIdPrefix}-type-cell`}>
            <Smartphone size={14} /> Cell
          </button>
          <button type="button"
            className={`phone-type-btn ${phoneType === 'work' ? 'active' : ''}`}
            onClick={() => handleTypeChange('work')}
            data-testid={`${testIdPrefix}-type-work`}>
            <Briefcase size={14} /> Work
          </button>
        </div>
      )}
      <div className="phone-input-row">
        <div className="phone-country-prefix">
          <span className="flag-icon">CA</span>
          <span>+1</span>
        </div>
        <input
          type="tel"
          className={`form-input phone-number-input ${showError ? 'input-error' : ''}`}
          value={displayValue}
          onChange={handleNumberChange}
          onBlur={() => setTouched(true)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          data-testid={`${testIdPrefix}-input`}
          autoComplete="tel-national"
        />
        {phoneType === 'work' && (
          <div className="phone-ext-group">
            <span className="ext-label">Ext.</span>
            <input
              type="text"
              className="form-input phone-ext-input"
              value={ext}
              onChange={handleExtChange}
              placeholder="123"
              disabled={disabled}
              data-testid={`${testIdPrefix}-ext-input`}
            />
          </div>
        )}
      </div>
      {showError && (
        <p className="phone-error" data-testid={`${testIdPrefix}-error`}>
          Please enter a valid 10-digit Canadian phone number
        </p>
      )}

    </div>
  );
};

export default CanadianPhoneInput;
